/**
 * Answer matching for Konjugation — deliberately NOT `core/normalize`.
 *
 * The shared `isCorrectAnswer` accepts anything within Levenshtein distance 1,
 * which is right for a translation: a one-character slip on "cachorro" should
 * not cost a streak. Here it is precisely wrong. The lesson of `du fährst` IS
 * the umlaut, `hältst` differs from `hälst` by one character, and `arbeitest`
 * from `arbeitet` by one. Every distinctive ending in the German present tense
 * sits inside the tolerance the shared matcher grants.
 *
 * So this compares exactly, after three normalisations that cannot hide a
 * conjugation error:
 *
 *   • surrounding whitespace, which is typing, not knowledge;
 *   • runs of internal whitespace, so "fahre  ab" passes — the answer is two
 *     words either way and the second space is a slip;
 *   • case, because German verbs are lowercase and a stray capital from a
 *     phone keyboard is not a claim about the verb.
 *
 * Nothing else. Not umlaut folding (`fahrst` must fail against `fährst`), not
 * ß/ss (`heisst` must fail against `heißt` — they are different spellings and
 * the deck teaches the one the Wortliste prints).
 */

/** Trim, collapse internal whitespace, lowercase. */
export function normalizeForm(input: string): string {
  return input.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Exact match after normalisation. No edit distance, by design. */
export function isFormCorrect(input: string, expected: string): boolean {
  const typed = normalizeForm(input);
  if (!typed) return false;
  return typed === normalizeForm(expected);
}
